import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { CardModule } from 'primeng/card';
import { MessageModule } from 'primeng/message';
import { TagModule } from 'primeng/tag';
import { ControlPointStatusService } from '../../../../shared/services/control-points/control-point-status.service';

const STALE_THRESHOLD_MS = 30_000;

/**
 * Cash-desk control-points alarm panel. Reads the root-provided
 * ControlPointStatusService (15s poll). Renders all active control points
 * urgency-sorted (overdue → due_soon → ok), with paused rows visible but
 * greyed-out, a distinct red banner on config_error, and a stale indicator
 * (last-known statuses kept, never blanked) on poll failure.
 *
 * `now` is passed in from the host page's wall-clock ticker so the stale
 * computation refreshes without a second timer here.
 */
@Component({
  selector: 'app-control-points-status',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, TranslateModule, CardModule, MessageModule, TagModule],
  templateUrl: './control-points-status.component.html',
  styleUrl: './control-points-status.component.scss',
})
export class ControlPointsStatusComponent {
  #service = inject(ControlPointStatusService);

  readonly statuses = this.#service.statuses;
  readonly configError = this.#service.configError;
  readonly lastFetchedAt = this.#service.lastFetchedAt;
  readonly lastError = this.#service.lastError;

  /** A poll has failed AND the last successful fetch is older than the
   *  staleness threshold (or there has been no successful fetch yet). */
  readonly isStale = computed(() => {
    const last = this.lastFetchedAt();
    if (this.lastError() === null) return false;
    if (last === null) return true;
    return Date.now() - last >= STALE_THRESHOLD_MS;
  });

  rowSeverity(
    status: string,
  ): 'success' | 'warn' | 'danger' | 'secondary' | 'contrast' {
    switch (status) {
      case 'overdue':
        return 'danger';
      case 'due_soon':
        return 'warn';
      case 'ok':
        return 'success';
      default:
        return 'secondary';
    }
  }

  statusLabelKey(status: string): string {
    return `controlPoints.status.states.${status}`;
  }
}
